import { useEffect, useState } from "react";
import { Menu, X, Sparkles } from "lucide-react";
import { NAV_LINKS, PROMO } from "@/lib/data";

const PROMO_KEY = `mv_promo_dismissed_${PROMO.version}`;

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showPromo, setShowPromo] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!PROMO.enabled) return;
    const dismissed = sessionStorage.getItem(PROMO_KEY);
    if (!dismissed) setShowPromo(true);
  }, []);

  const dismissPromo = () => {
    sessionStorage.setItem(PROMO_KEY, "1");
    setShowPromo(false);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[#F9F8F6]/85 border-b border-[#E6E2DA]"
          : "bg-transparent"
      }`}
    >
      {showPromo && (
        <div
          data-testid="promo-banner"
          className="bg-[#2A2A2A] text-[#F9F8F6] border-b border-[#C48B71]/40"
        >
          <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-16">
            <div className="flex items-center justify-between gap-4 py-2.5">
              <a
                href={PROMO.cta.href}
                data-testid="promo-banner-cta"
                className="flex items-center gap-3 group min-w-0"
              >
                <Sparkles
                  size={14}
                  strokeWidth={1.5}
                  className="text-[#C48B71] shrink-0"
                />
                <span className="overline text-[#C48B71] hidden sm:inline shrink-0">
                  {PROMO.eyebrow}
                </span>
                <span className="hidden sm:inline text-[#C48B71]/40">·</span>
                <span className="text-[0.82rem] sm:text-[0.88rem] font-light tracking-[0.02em] text-[#F9F8F6] truncate">
                  {PROMO.message}
                </span>
                <span className="hidden md:inline overline text-[#C48B71] link-underline shrink-0 ml-2 group-hover:text-[#F9F8F6] transition-colors">
                  {PROMO.cta.label} →
                </span>
              </a>
              <button
                onClick={dismissPromo}
                data-testid="promo-banner-close"
                aria-label="Dismiss promo"
                className="text-[#F9F8F6]/60 hover:text-[#F9F8F6] transition-colors p-1 shrink-0"
              >
                <X size={16} strokeWidth={1.4} />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-16">
        <div className="flex h-20 items-center justify-between">
          {/* Left links (desktop) */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.slice(0, 3).map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="overline link-underline text-[#2A2A2A]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* Logo */}
          <a
            href="#home"
            data-testid="nav-logo"
            className="serif text-2xl md:text-3xl tracking-tight text-[#2A2A2A] flex items-baseline gap-1"
          >
            <span className="italic font-light">Mane</span>
            <span className="font-normal">Vzion</span>
            <span className="text-[#C48B71] text-lg">.</span>
          </a>

          {/* Right links + book button */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.slice(3, 6).map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="overline link-underline text-[#2A2A2A]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 text-[#2A2A2A]"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.2} /> : <Menu size={22} strokeWidth={1.2} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-[#E6E2DA] bg-[#F9F8F6]"
        >
          <nav className="flex flex-col px-8 py-6 gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="serif text-2xl text-[#2A2A2A]"
              >
                {l.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Nav;
